import React, { useMemo } from 'react';
import { sysColor, famColor } from './palette';

// Four figures, one per claim, each DRAWN FROM THE NUMBER IT ILLUSTRATES.
// The icon font they replace said nothing: a Rust cog above "written in Rust"
// is the sentence again in a smaller typeface. A figure whose geometry is a
// function of the repository's own measurements cannot be swapped between
// panels without becoming wrong, which is the test these have to pass.
//
// Every one is inline SVG on a 120x64 viewBox. Each is coloured by what its
// parts mean (a strand is a subsystem, a cell a syscall family, a rail an
// architecture, a bar a day's heat), using the same hues as the chips and
// charts elsewhere on the page. Until the history arrives each falls back to
// currentColor, the card's own accent.

const W = 120, H = 64;

// Deterministic noise: the same repository always draws the same figure, so a
// screenshot taken today matches the site tomorrow.
function rng(seed) {
  let s = seed >>> 0 || 1;
  return () => (s = (s * 1664525 + 1013904223) >>> 0) / 4294967296;
}

// Split `total` whole units across rows by `share`, largest remainder first, so
// the parts always add back up to the whole.
function allocate(rows, total, key) {
  const raw = rows.map((r) => r[key] * total);
  const out = raw.map(Math.floor);
  let left = total - out.reduce((a, b) => a + b, 0);
  const order = raw.map((v, i) => [v - Math.floor(v), i]).sort((a, b) => b[0] - a[0]);
  for (let k = 0; left > 0 && k < order.length; k += 1, left -= 1) out[order[k][1]] += 1;
  return out;
}

// ---------------------------------------------------------------------------
// 1. Nothing is forked — a lattice woven from one continuous thread.
// The strand count is the crate count binned; nothing enters from outside the
// frame, which is the claim: no imported tree, one fabric.
function Lattice({ crates, systems }) {
  const strands = Math.max(6, Math.min(14, Math.round(crates / 16)));
  // Each warp strand is a subsystem, in proportion to how many crates it owns.
  const colours = useMemo(() => {
    if (!systems) return null;
    const counts = allocate(systems, strands + 1, 'crateShare');
    return systems.flatMap((s, i) => Array(counts[i]).fill(sysColor(s.id)));
  }, [systems, strands]);
  const { warp, weft } = useMemo(() => {
    const step = W / strands;
    const warp = [];
    for (let i = 0; i <= strands; i++) {
      const x = i * step;
      warp.push(`M${x.toFixed(1)} 4 C ${(x + step / 2).toFixed(1)} ${H / 3}, ${(x - step / 2).toFixed(1)} ${(2 * H) / 3}, ${x.toFixed(1)} ${H - 4}`);
    }
    let weft = '';
    for (let i = 1; i < 5; i++) {
      const y = (H / 5) * i;
      weft += `M0 ${y.toFixed(1)} C ${W / 3} ${(y - 7).toFixed(1)}, ${(2 * W) / 3} ${(y + 7).toFixed(1)}, ${W} ${y.toFixed(1)} `;
    }
    return { warp, weft };
  }, [strands]);
  return (
    <svg className="ox-fig" viewBox={`0 0 ${W} ${H}`} aria-hidden="true">
      <path d={weft} fill="none" stroke="currentColor" strokeWidth="0.7" opacity="0.55" />
      {warp.map((d, i) => (
        <path key={i} d={d} fill="none" stroke={colours?.[i] ?? 'currentColor'} strokeWidth="1.1" opacity="0.95" />
      ))}
    </svg>
  );
}

// ---------------------------------------------------------------------------
// 2. It runs Linux software — the syscall table as a filled grid.
// One cell per implemented call, laid out in the shape of a table because that
// is what it is. The count is live, so the figure fills in as the ABI widens.
function SyscallGrid({ syscalls, families }) {
  const cols = 24, rows = 8, total = cols * rows;
  const filled = Math.max(0, Math.min(total, Math.round((syscalls / 400) * total)));
  // The filled cells are shared out between the table's families in the order
  // the legend lists them, so the grid is a small version of the band's bar.
  const fills = useMemo(() => {
    if (!families) return null;
    const counts = allocate(families, filled, 'share');
    return families.flatMap((f, i) => Array(counts[i]).fill(famColor(f.id)));
  }, [families, filled]);
  const cells = useMemo(() => {
    const r = rng(syscalls);
    return Array.from({ length: total }, (_, i) => ({
      // Column-major, so each family fills whole columns left to right and
      // reads as a band rather than as confetti.
      x: Math.floor(i / rows) * (W / cols),
      y: (i % rows) * (H / rows),
      on: i < filled,
      o: 0.6 + r() * 0.4,
    }));
  }, [filled, syscalls, total]);
  const cw = W / cols - 0.9, ch = H / rows - 0.9;
  return (
    <svg className="ox-fig" viewBox={`0 0 ${W} ${H}`} aria-hidden="true">
      {cells.map((c, i) => (
        <rect key={i} x={c.x} y={c.y} width={cw} height={ch} rx="0.6"
              fill={c.on ? (fills?.[i] ?? 'currentColor') : 'none'}
              stroke={c.on ? 'none' : 'currentColor'}
              strokeWidth="0.35"
              opacity={c.on ? c.o : 0.22} />
      ))}
    </svg>
  );
}

// ---------------------------------------------------------------------------
// 3. Two architectures, one codebase — two rails converging on one spine.
// Deliberately symmetric: neither arch is the port. The rungs are the shared
// modules both sides compile.
function TwoRails() {
  const rungs = 9;
  return (
    <svg className="ox-fig" viewBox={`0 0 ${W} ${H}`} aria-hidden="true">
      <defs>
        <linearGradient id="ox-rails-spine" x1="0" x2="1" y1="0" y2="0">
          <stop offset="0" stopColor="var(--arch-x86)" />
          <stop offset="0.5" stopColor="var(--arch-arm)" />
          <stop offset="1" stopColor="var(--ox-rust-2)" />
        </linearGradient>
      </defs>
      {Array.from({ length: rungs }, (_, i) => {
        const t = (i + 1) / (rungs + 1);
        const x = 2 + t * (W / 2 - 2);
        const spread = (1 - t * t) * (H / 2 - 6);
        return (
          <line key={i} x1={x} y1={H / 2 - spread} x2={x} y2={H / 2 + spread}
                stroke="currentColor" strokeWidth="0.5" opacity="0.35" />
        );
      })}
      <path d={`M2 6 C ${W * 0.38} 6, ${W * 0.42} ${H / 2}, ${W / 2} ${H / 2}`}
            fill="none" stroke="var(--arch-x86)" strokeWidth="1.6" />
      <path d={`M2 ${H - 6} C ${W * 0.38} ${H - 6}, ${W * 0.42} ${H / 2}, ${W / 2} ${H / 2}`}
            fill="none" stroke="var(--arch-arm)" strokeWidth="1.6" />
      <path d={`M${W / 2} ${H / 2} L${W - 2} ${H / 2}`}
            fill="none" stroke="url(#ox-rails-spine)" strokeWidth="2" />
      <text x="4" y="3" className="ox-fig__label" fill="var(--arch-x86)">x86_64</text>
      <text x="4" y={H + 1.5} className="ox-fig__label" fill="var(--arch-arm)">aarch64</text>
      <circle cx={W / 2} cy={H / 2} r="2.6" fill="var(--ox-rust-2)" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// 4. AI wrote it, a human aimed it — one column per day, height and heat by
// that day's commits (history-activity.json). Before the history arrives it
// draws a seeded stand-in of the same shape; it never claims to be the data.
const HEAT = ['var(--heat-1)', 'var(--heat-2)', 'var(--heat-3)', 'var(--heat-4)', 'var(--heat-5)'];
export const heatOf = (value, max) => (value <= 0 ? null : HEAT[Math.min(4, Math.floor((value / max) * 5))]);

function Cadence({ commits, days, activity }) {
  // With the history loaded this is the real thing: one bar per day of the
  // project's life, height and heat both from that day's commit count.
  const real = activity?.perDay?.length ? activity.perDay : null;
  const bars = real ? real.length : 40;
  const heights = useMemo(() => {
    if (real) {
      const max = Math.max(...real) || 1;
      return real.map((v) => Math.max(0.04, v / max));
    }
    const r = rng(commits);
    return Array.from({ length: bars }, (_, i) => {
      const t = i / (bars - 1);
      const envelope = 0.35 + 0.65 * Math.pow(Math.sin(Math.PI * (0.15 + t * 0.85)), 0.6);
      return Math.max(0.08, Math.min(1, envelope * (0.55 + r() * 0.75)));
    });
  }, [commits, days, real, bars]);
  const bw = Math.max(0.4, W / bars - (real ? 0.35 : 1.1));
  return (
    <svg className="ox-fig" viewBox={`0 0 ${W} ${H}`} aria-hidden="true">
      {heights.map((h, i) => (
        <rect key={i} x={i * (W / bars)} y={H - h * (H - 4)} width={bw} height={h * (H - 4)}
              rx={real ? 0.25 : 0.7}
              fill={real ? (heatOf(h, 1) ?? 'var(--ox-line-2)') : 'currentColor'}
              opacity={real ? 0.95 : 0.25 + h * 0.6} />
      ))}
      <line x1="0" y1={H - 0.5} x2={W} y2={H - 0.5} stroke="currentColor" strokeWidth="0.5" opacity="0.35" />
    </svg>
  );
}

export const FIGURES = { Lattice, SyscallGrid, TwoRails, Cadence };

export default function Figure({ kind, stats, data = {} }) {
  switch (kind) {
    case 'lattice':  return <Lattice crates={stats.crates} systems={data.systems} />;
    case 'syscalls': return <SyscallGrid syscalls={stats.syscalls} families={data.table?.families} />;
    case 'rails':    return <TwoRails />;
    case 'cadence':  return <Cadence commits={stats.commits} days={stats.days} activity={data.activity} />;
    default:         return null;
  }
}
